import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  Button,
  Switch,
  Input,
  Space,
  Typography,
  message,
  Spin,
  Modal,
  Drawer,
  Checkbox,
  Table,
  Tag,
  Popconfirm,
  Select,
  Divider,
  Row,
  Col,
  Empty,
  Alert,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  CopyOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import WebhookService, {
  WebhookSettings,
  WebhookEndpoint,
  WebhookEventDef,
  WebhookDelivery,
  WebhookTestResult,
  PaginatedMeta,
} from '../../services/webhooks';

const { Title, Text, Paragraph } = Typography;

// category 中文名映射
const CATEGORY_LABELS: Record<string, string> = {
  payment: '支付',
  refund: '退款',
  dispute: '争议',
  checkout: '结账',
  customer: '客户',
  payment_method: '支付方式',
  payout: '出款',
  settlement: '结算',
  subscription: '订阅',
  invoice: '发票',
  onboarding: '入驻',
};

const maskSecret = (secret: string): string => {
  if (!secret) return '';
  if (secret.length <= 8) return '••••••••';
  return `${secret.slice(0, 6)}${'•'.repeat(Math.min(20, secret.length - 10))}${secret.slice(-4)}`;
};

const WebhooksTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<WebhookSettings | null>(null);
  const [regenerate, setRegenerate] = useState(false); // 提交时把 secret 置空
  const [showSecret, setShowSecret] = useState(false);

  const [catalog, setCatalog] = useState<WebhookEventDef[]>([]);

  // 事件选择弹窗
  const [eventDrawerOpen, setEventDrawerOpen] = useState(false);
  const [editingEndpointIndex, setEditingEndpointIndex] = useState<number>(-1);

  // 测试
  const [testUrl, setTestUrl] = useState<string>('');
  const [testEvent, setTestEvent] = useState<string>('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<WebhookTestResult | null>(null);

  // 投递日志
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [deliveryMeta, setDeliveryMeta] = useState<PaginatedMeta>({
    total: 0,
    per_page: 20,
    current_page: 1,
    last_page: 1,
  });
  const [deliveriesLoading, setDeliveriesLoading] = useState(false);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        WebhookService.getSettings(),
        WebhookService.getEventCatalog().catch(() => []),
      ]);
      setSettings(s);
      setCatalog(c);
      setRegenerate(false);
    } catch (e) {
      message.error('加载 Webhook 设置失败');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDeliveries = useCallback(async (page: number = 1, perPage: number = 20) => {
    setDeliveriesLoading(true);
    try {
      const res = await WebhookService.listDeliveries(page, perPage);
      setDeliveries(res.data);
      setDeliveryMeta(res.meta);
    } catch (e) {
      message.error('加载投递日志失败');
    } finally {
      setDeliveriesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
    loadDeliveries(1);
  }, [loadSettings, loadDeliveries]);

  // 按 category 分组的事件目录
  const groupedCatalog = useMemo(() => {
    const groups: Record<string, WebhookEventDef[]> = {};
    catalog.forEach((evt) => {
      const cat = evt.category || 'other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(evt);
    });
    return groups;
  }, [catalog]);

  const allEventTypes = useMemo(() => catalog.map((e) => e.type), [catalog]);

  // ---- endpoint 操作 ----
  const updateEndpoint = (index: number, patch: Partial<WebhookEndpoint>) => {
    if (!settings) return;
    const endpoints = settings.endpoints.map((ep, i) =>
      i === index ? { ...ep, ...patch } : ep
    );
    setSettings({ ...settings, endpoints });
  };

  const addEndpoint = () => {
    if (!settings) return;
    setSettings({
      ...settings,
      endpoints: [
        ...settings.endpoints,
        { url: '', description: '', event_types: [], active: true },
      ],
    });
  };

  const removeEndpoint = (index: number) => {
    if (!settings) return;
    setSettings({
      ...settings,
      endpoints: settings.endpoints.filter((_, i) => i !== index),
    });
  };

  const openEventDrawer = (index: number) => {
    setEditingEndpointIndex(index);
    setEventDrawerOpen(true);
  };

  // ---- 保存 ----
  const handleSave = async () => {
    if (!settings) return;
    // 基本校验
    for (const ep of settings.endpoints) {
      if (!ep.url || !/^https?:\/\//i.test(ep.url)) {
        message.error('请填写有效的 Endpoint URL（需以 http:// 或 https:// 开头）');
        return;
      }
    }
    setSaving(true);
    try {
      const payload: WebhookSettings = {
        ...settings,
        secret_key: regenerate ? '' : settings.secret_key,
      };
      const updated = await WebhookService.updateSettings(payload);
      setSettings(updated);
      setRegenerate(false);
      setShowSecret(false);
      message.success(regenerate ? 'Webhook 设置已保存，签名密钥已重新生成' : 'Webhook 设置已保存');
    } catch (e) {
      message.error('保存失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  const handleCopySecret = () => {
    if (!settings?.secret_key) return;
    navigator.clipboard.writeText(settings.secret_key);
    message.success('签名密钥已复制到剪贴板');
  };

  // ---- 测试 ----
  const handleTest = async () => {
    if (!testUrl) {
      message.error('请选择要测试的 Endpoint');
      return;
    }
    if (!testEvent) {
      message.error('请选择测试事件类型');
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const result = await WebhookService.sendTest(testUrl, testEvent);
      setTestResult(result);
      const ok = result.success ?? false;
      if (ok) {
        message.success('测试事件已发送');
      } else {
        message.warning('测试事件已发送，但端点未返回成功状态');
      }
    } catch (e) {
      message.error('发送测试事件失败');
    } finally {
      setTesting(false);
    }
  };

  // ---- 投递日志列 ----
  const deliveryColumns = [
    {
      title: '时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 170,
      render: (v: string) => (v ? new Date(v).toLocaleString('zh-CN') : '-'),
    },
    {
      title: '事件类型',
      dataIndex: 'event_type',
      key: 'event_type',
      render: (v: string) => <Text code>{v}</Text>,
    },
    {
      title: 'Endpoint',
      dataIndex: 'endpoint',
      key: 'endpoint',
      ellipsis: true,
    },
    {
      title: '状态',
      key: 'status',
      width: 130,
      render: (_: any, record: WebhookDelivery) =>
        record.success ? (
          <Tag color="success">成功 {record.response_status || ''}</Tag>
        ) : (
          <Tag color="error">失败 {record.response_status || ''}</Tag>
        ),
    },
    {
      title: '耗时',
      dataIndex: 'duration_ms',
      key: 'duration_ms',
      width: 90,
      render: (v: number) => (v != null ? `${v} ms` : '-'),
    },
    {
      title: '尝试次数',
      dataIndex: 'attempt_number',
      key: 'attempt_number',
      width: 90,
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!settings) {
    return <Empty description="无法加载 Webhook 设置" />;
  }

  const editingEndpoint =
    editingEndpointIndex >= 0 ? settings.endpoints[editingEndpointIndex] : undefined;
  const editingSubscribeAll = editingEndpoint
    ? editingEndpoint.event_types.length === 0
    : false;

  return (
    <div>
      {/* ===================== A. Endpoint 配置 ===================== */}
      <Card
        title={
          <Space>
            <ThunderboltOutlined />
            <span>Endpoint 配置</span>
          </Space>
        }
        extra={
          <Space>
            <Switch
              checked={settings.enabled}
              onChange={(checked) => setSettings({ ...settings, enabled: checked })}
            />
            <Text>启用 Webhook 推送</Text>
          </Space>
        }
        style={{ marginBottom: 24 }}
      >
        <Paragraph type="secondary">
          配置接收事件通知的 HTTP(S) 端点。每个端点可订阅指定的事件类型，留空表示接收全部事件。
        </Paragraph>

        {settings.endpoints.length === 0 && (
          <Empty
            description="尚未配置任何 Endpoint"
            style={{ margin: '24px 0' }}
          />
        )}

        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {settings.endpoints.map((ep, index) => (
            <Card
              key={index}
              size="small"
              type="inner"
              title={`Endpoint ${index + 1}`}
              extra={
                <Space>
                  <Switch
                    checkedChildren="启用"
                    unCheckedChildren="停用"
                    checked={ep.active}
                    onChange={(checked) => updateEndpoint(index, { active: checked })}
                  />
                  <Popconfirm
                    title="确定删除该 Endpoint 吗？"
                    onConfirm={() => removeEndpoint(index)}
                    okText="删除"
                    cancelText="取消"
                  >
                    <Button danger size="small" icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              }
            >
              <Row gutter={16}>
                <Col xs={24} md={14}>
                  <Text type="secondary">Endpoint URL</Text>
                  <Input
                    placeholder="https://example.com/webhooks"
                    value={ep.url}
                    onChange={(e) => updateEndpoint(index, { url: e.target.value })}
                    style={{ marginTop: 4, marginBottom: 12 }}
                  />
                </Col>
                <Col xs={24} md={10}>
                  <Text type="secondary">描述</Text>
                  <Input
                    placeholder="可选，便于识别用途"
                    value={ep.description}
                    onChange={(e) => updateEndpoint(index, { description: e.target.value })}
                    style={{ marginTop: 4, marginBottom: 12 }}
                  />
                </Col>
              </Row>
              <Space wrap>
                <Button onClick={() => openEventDrawer(index)}>
                  订阅事件
                </Button>
                {ep.event_types.length === 0 ? (
                  <Tag color="blue">接收所有事件</Tag>
                ) : (
                  <Text type="secondary">已订阅 {ep.event_types.length} 个事件</Text>
                )}
              </Space>
            </Card>
          ))}
        </Space>

        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={addEndpoint}
          style={{ marginTop: 16, width: '100%' }}
        >
          添加 Endpoint
        </Button>

        <Divider />

        {/* 签名密钥 */}
        <Title level={5}>签名密钥 (Signing secret)</Title>
        <Paragraph type="secondary">
          使用该密钥验证 Webhook 请求的签名，确保事件确实来自本平台。请妥善保管，切勿泄露。
        </Paragraph>
        {regenerate ? (
          <Alert
            type="warning"
            showIcon
            message="保存后将重新生成签名密钥，原有密钥与签名将立即失效。"
            style={{ marginBottom: 12 }}
          />
        ) : null}
        <Space wrap>
          <Input
            readOnly
            style={{ width: 360 }}
            value={
              regenerate
                ? '（保存后生成新密钥）'
                : showSecret
                ? settings.secret_key
                : maskSecret(settings.secret_key)
            }
          />
          <Button
            icon={showSecret ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            onClick={() => setShowSecret((v) => !v)}
            disabled={regenerate || !settings.secret_key}
          >
            {showSecret ? '隐藏' : '显示'}
          </Button>
          <Button
            icon={<CopyOutlined />}
            onClick={handleCopySecret}
            disabled={regenerate || !settings.secret_key}
          >
            复制
          </Button>
          <Popconfirm
            title="重新生成签名密钥"
            description="重新生成后，旧密钥与基于旧密钥的签名将立即失效。确定继续吗？"
            onConfirm={() => {
              setRegenerate(true);
              setShowSecret(false);
            }}
            okText="确定"
            cancelText="取消"
          >
            <Button icon={<ReloadOutlined />} disabled={regenerate}>
              重新生成密钥
            </Button>
          </Popconfirm>
          {regenerate && (
            <Button onClick={() => setRegenerate(false)}>取消重新生成</Button>
          )}
        </Space>

        <Divider />

        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
          保存
        </Button>
      </Card>

      {/* ===================== B. 发送测试 ===================== */}
      <Card title="发送测试" style={{ marginBottom: 24 }}>
        <Paragraph type="secondary">
          向选定的 Endpoint 发送一条测试事件，验证端点是否能正常接收。
        </Paragraph>
        <Space wrap align="start">
          <Select
            placeholder="选择 Endpoint"
            style={{ width: 320 }}
            value={testUrl || undefined}
            onChange={setTestUrl}
            options={settings.endpoints
              .filter((ep) => ep.url)
              .map((ep) => ({
                label: ep.description ? `${ep.url}（${ep.description}）` : ep.url,
                value: ep.url,
              }))}
          />
          <Select
            placeholder="选择事件类型"
            style={{ width: 280 }}
            showSearch
            value={testEvent || undefined}
            onChange={setTestEvent}
            options={catalog.map((e) => ({
              label: `${e.type} - ${e.description}`,
              value: e.type,
            }))}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            loading={testing}
            onClick={handleTest}
          >
            发送测试事件
          </Button>
        </Space>

        {testResult && (
          <div style={{ marginTop: 16 }}>
            <Space style={{ marginBottom: 8 }}>
              <Text strong>测试结果：</Text>
              {testResult.success ? (
                <Tag color="success">
                  成功 {testResult.response_status ?? testResult.status_code ?? ''}
                </Tag>
              ) : (
                <Tag color="error">
                  失败 {testResult.response_status ?? testResult.status_code ?? ''}
                </Tag>
              )}
              {testResult.duration_ms != null && (
                <Text type="secondary">耗时 {testResult.duration_ms} ms</Text>
              )}
            </Space>
            {testResult.error && (
              <Alert type="error" showIcon message={testResult.error} style={{ marginBottom: 8 }} />
            )}
            <pre
              style={{
                background: '#fafafa',
                border: '1px solid #f0f0f0',
                borderRadius: 4,
                padding: 12,
                maxHeight: 320,
                overflow: 'auto',
                fontSize: 12,
              }}
            >
              {JSON.stringify(testResult, null, 2)}
            </pre>
          </div>
        )}
      </Card>

      {/* ===================== C. 投递日志 ===================== */}
      <Card
        title="投递日志"
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={() => loadDeliveries(deliveryMeta.current_page, deliveryMeta.per_page)}
          >
            刷新
          </Button>
        }
      >
        <Table<WebhookDelivery>
          rowKey="id"
          loading={deliveriesLoading}
          columns={deliveryColumns}
          dataSource={deliveries}
          locale={{ emptyText: '暂无投递记录' }}
          expandable={{
            expandedRowRender: (record) => (
              <div style={{ display: 'grid', gap: 12 }}>
                {record.error && (
                  <Alert type="error" showIcon message={`错误：${record.error}`} />
                )}
                <div>
                  <Text strong>Payload</Text>
                  <pre style={preStyle}>{formatJson(record.payload)}</pre>
                </div>
                <div>
                  <Text strong>Response Body</Text>
                  <pre style={preStyle}>{formatJson(record.response_body)}</pre>
                </div>
              </div>
            ),
          }}
          pagination={{
            current: deliveryMeta.current_page,
            pageSize: deliveryMeta.per_page,
            total: deliveryMeta.total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => loadDeliveries(page, pageSize),
          }}
        />
      </Card>

      {/* ===================== 订阅事件抽屉 ===================== */}
      <Drawer
        title="订阅事件"
        width={520}
        open={eventDrawerOpen}
        onClose={() => setEventDrawerOpen(false)}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Button type="primary" onClick={() => setEventDrawerOpen(false)}>
              完成
            </Button>
          </div>
        }
      >
        {editingEndpoint && (
          <>
            <Checkbox
              checked={editingSubscribeAll}
              onChange={(e) => {
                if (e.target.checked) {
                  updateEndpoint(editingEndpointIndex, { event_types: [] });
                } else {
                  // 取消「接收所有」时，默认全选所有具体事件
                  updateEndpoint(editingEndpointIndex, { event_types: [...allEventTypes] });
                }
              }}
            >
              <Text strong>接收所有事件</Text>
            </Checkbox>
            <Paragraph type="secondary" style={{ marginTop: 8 }}>
              勾选「接收所有事件」后，该端点将自动接收未来新增的事件类型。
            </Paragraph>

            <Divider />

            <Space style={{ marginBottom: 12 }}>
              <Button
                size="small"
                disabled={editingSubscribeAll}
                onClick={() =>
                  updateEndpoint(editingEndpointIndex, { event_types: [...allEventTypes] })
                }
              >
                全选
              </Button>
              <Button
                size="small"
                disabled={editingSubscribeAll}
                onClick={() => updateEndpoint(editingEndpointIndex, { event_types: [] })}
              >
                全不选
              </Button>
            </Space>

            <div style={{ opacity: editingSubscribeAll ? 0.45 : 1 }}>
              <Checkbox.Group
                value={editingEndpoint.event_types}
                onChange={(checked) =>
                  updateEndpoint(editingEndpointIndex, {
                    event_types: checked as string[],
                  })
                }
                style={{ width: '100%' }}
                disabled={editingSubscribeAll}
              >
                {Object.entries(groupedCatalog).map(([cat, events]) => (
                  <div key={cat} style={{ marginBottom: 16 }}>
                    <Text strong>{CATEGORY_LABELS[cat] || cat}</Text>
                    <div style={{ marginTop: 8 }}>
                      {events.map((evt) => (
                        <div key={evt.type} style={{ marginBottom: 6 }}>
                          <Checkbox value={evt.type}>
                            <Text code>{evt.type}</Text>
                            <span style={{ marginLeft: 8, color: '#888' }}>
                              {evt.description}
                            </span>
                          </Checkbox>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </Checkbox.Group>
              {catalog.length === 0 && <Empty description="无法加载事件目录" />}
            </div>
          </>
        )}
      </Drawer>
    </div>
  );
};

const preStyle: React.CSSProperties = {
  background: '#fafafa',
  border: '1px solid #f0f0f0',
  borderRadius: 4,
  padding: 12,
  maxHeight: 320,
  overflow: 'auto',
  fontSize: 12,
  margin: '4px 0 0',
};

function formatJson(value?: string): string {
  if (!value) return '（空）';
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
}

export default WebhooksTab;
